import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Clock, CalendarDays, CalendarPlus, User, AlertTriangle, CheckCircle2, Sparkles, Stethoscope } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import useDocumentMeta from "@/lib/useDocumentMeta";

const STEPS = ["Услуга", "Дата", "Час", "Данни"];

const iconFor = (id) => (id.startsWith("detail") ? Sparkles : Stethoscope);

function pad2(n) { return n.toString().padStart(2, "0"); }
function todayISO() {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function addDaysISO(iso, days) {
    const d = new Date(iso);
    d.setDate(d.getDate() + days);
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function formatBG(iso) {
    const d = new Date(iso);
    return d.toLocaleDateString("bg-BG", { weekday: "long", day: "2-digit", month: "long" });
}
function formatDuration(mins) {
    if (mins < 60) return `${mins} мин`;
    if (mins % 60 === 0) return `${mins / 60} ${mins / 60 === 1 ? "час" : "часа"}`;
    return `${Math.floor(mins / 60)} ч ${mins % 60} мин`;
}

export default function BookingPage() {
    useDocumentMeta({
        title: "Онлайн резервация · Konstantin Mobile",
        description: "Запазете час онлайн за детайлинг или диагностика в Konstantin Mobile, Петрич. Автоматично изчисляване на свободни часове.",
        path: "/services/booking",
    });

    const [step, setStep] = useState(0);
    const [services, setServices] = useState([]);
    const [service, setService] = useState(null);
    const [searchParams] = useSearchParams();
    const initialCategory = searchParams.get("category"); // "detailing" | "diagnostics"
    const [date, setDate] = useState("");
    const [slots, setSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [slot, setSlot] = useState("");
    const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
    const [status, setStatus] = useState({ loading: false, ok: null, error: "" });

    useEffect(() => { api.get("/booking/services").then((r) => setServices(r.data || [])); }, []);

    // Filter services by category (?category=detailing|diagnostics)
    const visibleServices = useMemo(() => {
        if (!initialCategory) return services;
        if (initialCategory === "diagnostics") return services.filter((s) => s.staff_id === "diagnostics");
        if (initialCategory === "detailing") return services.filter((s) => s.staff_id === "detailing");
        return services;
    }, [services, initialCategory]);

    // Auto-select when the filter narrows to a single service (e.g. diagnostics) — skip step 0
    useEffect(() => {
        if (service || visibleServices.length !== 1) return;
        setService(visibleServices[0]);
        setStep((s) => (s === 0 ? 1 : s));
    }, [visibleServices, service]);

    useEffect(() => {
        if (!service || !date) { setSlots([]); return; }
        setSlotsLoading(true); setSlot("");
        api.get("/booking/slots", { params: { service_id: service.id, date } })
            .then((r) => setSlots(r.data.slots || []))
            .finally(() => setSlotsLoading(false));
    }, [service, date]);

    const upcomingDays = useMemo(() => {
        const today = todayISO();
        return Array.from({ length: 14 }, (_, i) => addDaysISO(today, i))
            .filter((d) => { const w = new Date(d).getDay(); return w !== 0 && w !== 6; })
            .slice(0, 10);
    }, []);

    const canNext =
        (step === 0 && service) ||
        (step === 1 && date) ||
        (step === 2 && slot) ||
        (step === 3 && form.name && form.email && form.phone &&
            (service?.id !== "diagnostics" || form.notes.trim().length > 0));

    const submit = async () => {
        setStatus({ loading: true, ok: null, error: "" });
        try {
            const r = await api.post("/booking/bookings", {
                service_id: service.id,
                date,
                start_time: slot,
                customer_name: form.name,
                customer_email: form.email,
                customer_phone: form.phone,
                notes: form.notes || null,
            });
            setStatus({ loading: false, ok: true, error: "", data: r.data });
        } catch (err) {
            setStatus({
                loading: false, ok: false,
                error: formatApiError(err?.response?.data?.detail) || err.message,
            });
        }
    };

    if (status.ok) {
        const icsUrl = status.data?.ics_url;
        return (
            <main className="relative pt-32 pb-24 min-h-screen flex items-center justify-center px-6">
                <div className="max-w-md text-center bg-surface border border-line rounded-3xl px-10 py-12 shadow-soft">
                    <div className="w-16 h-16 mx-auto rounded-full bg-gold/10 border border-gold/30 text-gold flex items-center justify-center mb-6">
                        <CheckCircle2 size={32} />
                    </div>
                    <h2 className="font-display text-3xl mb-3">Резервацията е потвърдена!</h2>
                    <div className="text-ink-muted mb-2">{service.name}</div>
                    <div className="text-ink font-medium mb-6">{formatBG(date)}, {slot}</div>
                    <p className="text-sm text-ink-muted mb-8">
                        Изпратихме потвърждение на имейла Ви. Ще се видим скоро!
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
                        {icsUrl && (
                            <a
                                href={icsUrl}
                                data-testid="add-to-calendar-btn"
                                className="inline-flex items-center gap-2 border border-gold/50 text-gold hover:bg-gold hover:text-champagne px-6 py-3 rounded-full transition-all text-sm"
                            >
                                <CalendarPlus size={16} /> Добави в моя календар
                            </a>
                        )}
                        <Link to="/" className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-champagne px-7 py-3 rounded-full transition-all text-sm">
                            Към началната страница
                        </Link>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main className="relative pt-32 pb-24 min-h-screen" data-testid="booking-page">
            <div className="relative max-w-3xl mx-auto px-6 lg:px-10">
                <Link to="/" className="inline-flex items-center gap-2 text-ink-muted hover:text-gold text-sm mb-8">
                    <ArrowLeft size={16} /> Назад към сайта
                </Link>

                <div className="mb-8">
                    <span className="block text-gold text-[11px] uppercase tracking-[0.32em] mb-4">
                        Услуги · Онлайн резервация
                    </span>
                    <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-4">
                        Запазете своя час
                    </h1>
                </div>

                {/* Progress */}
                <div className="flex items-center justify-between mb-10">
                    {STEPS.map((label, i) => (
                        <div key={label} className="flex items-center flex-1">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                                i <= step ? "bg-gold text-champagne" : "bg-surface border border-line text-ink-subtle"
                            }`}>
                                {i < step ? <Check size={14} /> : i + 1}
                            </div>
                            <div className={`ml-2 text-xs uppercase tracking-widest hidden sm:inline ${
                                i === step ? "text-ink" : "text-ink-subtle"
                            }`}>{label}</div>
                            {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-3 ${i < step ? "bg-gold" : "bg-line"}`} />}
                        </div>
                    ))}
                </div>

                <div className="bg-surface border border-line rounded-3xl p-6 sm:p-8 shadow-soft min-h-[380px]">
                    <AnimatePresence mode="wait">
                        {step === 0 && (
                            <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                                <h2 className="font-display text-2xl mb-6">Изберете услуга</h2>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    {visibleServices.map((s) => {
                                        const Icon = iconFor(s.id);
                                        const active = service?.id === s.id;
                                        return (
                                            <button
                                                key={s.id}
                                                onClick={() => setService(s)}
                                                data-testid={`svc-${s.id}`}
                                                className={`text-left p-5 rounded-2xl border transition-all ${
                                                    active ? "border-gold bg-gold/5 shadow-gold" : "border-line hover:border-gold/40 bg-bg"
                                                }`}
                                            >
                                                <Icon size={20} className="text-gold mb-3" />
                                                <div className="font-medium text-ink">{s.name}</div>
                                                <div className="text-xs text-ink-muted mt-1 flex items-center gap-1">
                                                    <Clock size={12} /> {formatDuration(s.duration_minutes)}
                                                </div>
                                                <div className="text-sm text-ink-muted mt-3 leading-relaxed">{s.description}</div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </motion.div>
                        )}

                        {step === 1 && (
                            <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                                <h2 className="font-display text-2xl mb-2">Изберете дата</h2>
                                <p className="text-sm text-ink-muted mb-6">Работно време: Пон – Пет, 09:00 – 18:00</p>
                                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                                    {upcomingDays.map((d) => {
                                        const dt = new Date(d);
                                        const active = date === d;
                                        return (
                                            <button
                                                key={d}
                                                onClick={() => setDate(d)}
                                                data-testid={`date-${d}`}
                                                className={`p-4 rounded-2xl border text-center transition-all ${
                                                    active ? "border-gold bg-gold/5 shadow-gold" : "border-line hover:border-gold/40 bg-bg"
                                                }`}
                                            >
                                                <div className="text-[10px] uppercase tracking-widest text-ink-subtle">
                                                    {dt.toLocaleDateString("bg-BG", { weekday: "short" })}
                                                </div>
                                                <div className="text-lg font-medium mt-1">{dt.getDate()}</div>
                                                <div className="text-xs text-ink-muted">
                                                    {dt.toLocaleDateString("bg-BG", { month: "short" })}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </motion.div>
                        )}

                        {step === 2 && (
                            <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                                <h2 className="font-display text-2xl mb-2">Изберете свободен час</h2>
                                <p className="text-sm text-ink-muted mb-6">{formatBG(date)} · {service.name}</p>
                                {slotsLoading ? (
                                    <div className="text-ink-muted">Зареждане на свободни часове…</div>
                                ) : slots.length === 0 ? (
                                    <div className="text-ink-muted">Няма свободни часове за тази дата. Моля изберете друг ден.</div>
                                ) : (
                                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                                        {slots.map((t) => {
                                            const active = slot === t;
                                            return (
                                                <button
                                                    key={t}
                                                    onClick={() => setSlot(t)}
                                                    data-testid={`slot-${t}`}
                                                    className={`py-3 rounded-xl border text-sm transition-all ${
                                                        active ? "border-gold bg-gold text-champagne shadow-gold" : "border-line hover:border-gold/40 bg-bg text-ink"
                                                    }`}
                                                >
                                                    {t}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </motion.div>
                        )}

                        {step === 3 && (
                            <motion.div key="s4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                                <h2 className="font-display text-2xl mb-6">Вашите данни</h2>
                                <div className="space-y-4">
                                    {[
                                        ["name", "Име и фамилия", "text", true],
                                        ["phone", "Телефон", "tel", true],
                                        ["email", "Имейл", "email", true],
                                        ...(service?.id === "diagnostics"
                                            ? [["notes", "Опишете оплакването", "text", true]]
                                            : []),
                                    ].map(([k, label, type, req]) => (
                                        <label key={k} className="block bg-bg border border-line rounded-xl px-5 py-3 hover:border-gold/40 transition-all">
                                            <span className="block text-[10px] uppercase tracking-[0.25em] text-ink-subtle">{label} {req && "*"}</span>
                                            <input
                                                type={type}
                                                required={req}
                                                value={form[k]}
                                                onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                                                className="w-full mt-1 bg-transparent text-sm focus:outline-none"
                                                data-testid={`booking-${k}`}
                                            />
                                        </label>
                                    ))}
                                </div>

                                {/* Summary */}
                                <div className="mt-6 p-4 rounded-xl bg-bg border border-line text-sm space-y-1">
                                    <div><span className="text-ink-subtle">Услуга:</span> <span className="text-ink">{service.name}</span></div>
                                    <div><span className="text-ink-subtle">Дата:</span> <span className="text-ink">{formatBG(date)}</span></div>
                                    <div><span className="text-ink-subtle">Час:</span> <span className="text-ink">{slot} ({formatDuration(service.duration_minutes)})</span></div>
                                </div>

                                {/* Cancellation policy */}
                                <div className="mt-4 p-4 rounded-xl bg-gold/5 border border-gold/30 flex gap-3">
                                    <AlertTriangle size={18} className="text-gold shrink-0 mt-0.5" />
                                    <p className="text-xs text-ink leading-relaxed">
                                        <strong>Важно:</strong> Ако отмените своя запазен час по-късно от <strong>24 часа</strong> преди началото
                                        му или не се явите без предварително уведомление, при следваща резервация ще бъде
                                        необходимо предварително заплащане (капаро).
                                    </p>
                                </div>

                                {status.error && <div className="mt-4 text-sm text-red-600" data-testid="booking-error">{status.error}</div>}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Navigation */}
                <div className="mt-6 flex justify-between">
                    <button
                        type="button"
                        onClick={() => setStep((s) => Math.max(0, s - 1))}
                        disabled={step === 0}
                        className="inline-flex items-center gap-2 px-6 py-3 border border-line rounded-full text-sm text-ink-muted hover:border-gold/40 disabled:opacity-40"
                        data-testid="booking-back"
                    >
                        <ArrowLeft size={14} /> Назад
                    </button>
                    {step < 3 ? (
                        <button
                            type="button"
                            onClick={() => setStep((s) => s + 1)}
                            disabled={!canNext}
                            className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-champagne px-8 py-3 rounded-full text-sm font-medium disabled:opacity-40 shadow-gold"
                            data-testid="booking-next"
                        >
                            Напред <ArrowRight size={14} />
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={submit}
                            disabled={!canNext || status.loading}
                            className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-champagne px-8 py-3 rounded-full text-sm font-medium disabled:opacity-40 shadow-gold"
                            data-testid="booking-submit"
                        >
                            {status.loading ? "Изпращане…" : (<>Потвърди <Check size={14} /></>)}
                        </button>
                    )}
                </div>
            </div>
        </main>
    );
}
